import { memo, useCallback, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import TextInputField, { TextInputFieldProps } from 'components/textInput';
import Button, { ButtonProps } from 'components/button';
import CampaignFormField, {
  CampaignFormFieldProps,
} from '../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import {
  GuidelineRuleFormProps,
  GuidelineRuleFormField,
} from 'pages/campaigns/campaignForm/campaignForm.types';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { CampaignFormStepTwoContext } from '../../../campaignFormStepTwo.context';

interface SetGuidelineFormTagsValueProps {
  name: keyof GuidelineRuleFormProps;
  value?: GuidelineRuleFormField['value'];
  index?: number;
  isDelete?: boolean;
}

interface TagFieldProps
  extends Pick<CampaignFormFieldProps, 'label' | 'description'>,
    Pick<TextInputFieldProps, 'placeholder'> {
  tagFieldName: keyof GuidelineRuleFormProps;
  addButtonText: ButtonProps['text'];
}

const TagField = ({
  tagFieldName,
  label,
  description,
  placeholder,
  addButtonText,
}: TagFieldProps) => {
  const { brand, guidelineRuleFormMethods } = useContext(CampaignFormContext);
  const { removeTagFixes } = useContext(CampaignFormStepTwoContext);

  const tag = useWatch({
    control: guidelineRuleFormMethods?.control,
    name: tagFieldName,
  }) as GuidelineRuleFormField;

  const parsedPlaceholder = useMemo(() => {
    // atTag인 경우 @삭제
    if (tagFieldName === 'AT_TAGS') {
      return (brand?.atTag ?? '@brand_tag')
        .split(/\s+/)
        .filter(Boolean)
        .map((tag) => removeTagFixes(tag))[0];
    }

    return placeholder;
  }, [placeholder, brand?.atTag]);

  const setGuidelineFormTagValue = useCallback(
    ({
      name,
      value,
      index,
      isDelete = false,
    }: SetGuidelineFormTagsValueProps) => {
      const tagFormValue = guidelineRuleFormMethods.getValues(
        name,
      ) as GuidelineRuleFormField;
      const tagValues = (tagFormValue?.value as string[]) || [];
      const removedTagValue = removeTagFixes(value as string);

      if (isDelete) {
        tagValues.splice(index, 1);
      } else if (index >= 0) {
        tagValues[index] = removedTagValue;
      } else {
        tagValues.push(removedTagValue);
      }

      guidelineRuleFormMethods.setValue(name, {
        ...tagFormValue,
        value: tagValues,
      });
    },
    [],
  );

  return (
    <CampaignFormField
      as="ul"
      wrapperClassName="!gap-2.5"
      label={label}
      description={description}
    >
      {(tag?.value as string[])?.map((value, index) => {
        return (
          <li key={`${tagFieldName}-${index}`}>
            <TextInputField
              bordered
              value={value}
              onChangeText={(value) =>
                setGuidelineFormTagValue({ name: tagFieldName, value, index })
              }
              placeholder={parsedPlaceholder}
              icon={{
                position: 'right',
                icon: index > 0 && (
                  <div className="flex w-full h-full justify-center mr-[13px]">
                    <img
                      className="w-[22px] h-[22px] cursor-pointer my-auto"
                      src="/assets/icons/ic-close-black-squared.svg"
                      onClick={() =>
                        setGuidelineFormTagValue({
                          name: tagFieldName,
                          value,
                          index,
                          isDelete: true,
                        })
                      }
                    />
                  </div>
                ),
              }}
            />
          </li>
        );
      })}
      <Button
        className="border-1 border-gray-200 hover:border-black text-center py-3"
        justify="center"
        text={addButtonText}
        onClick={() =>
          setGuidelineFormTagValue({ name: tagFieldName, value: '' })
        }
      />
    </CampaignFormField>
  );
};

export default memo(TagField);
